import type { Dispatch } from "react";
import type { CellIdentifier, TableState } from "./types";
import type { EditorAction } from "../editor/reducers";
import type { TableEditorStep6Fragment } from "../client/generated";
import { encodeCellIdentifier } from "./utils";
import { mdiTableHeadersEye } from "@mdi/js";
import { TableEditorStep } from "./TableEditorStep";
import { useTranslation } from "react-i18next";
import { Button } from "@acdc2/ui/components/button";
import { TableCellPreview } from "./TableCellPreview";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@acdc2/ui/components/dropdown-menu";

type Props = {
  fragment: TableEditorStep6Fragment;
  dispatch: Dispatch<EditorAction>;
  tableState: TableState;
  commonHeaders: CellIdentifier[];
  isHeaderless: boolean;
  isAnyCellSelected: boolean;
};

export function TableEditorStep6({
  fragment,
  dispatch,
  tableState,
  commonHeaders,
  isHeaderless,
  isAnyCellSelected,
}: Props) {
  const { t } = useTranslation();
  const encodedCommonHeaders = commonHeaders.map(encodeCellIdentifier);

  return (
    <TableEditorStep
      value="step6"
      iconPath={mdiTableHeadersEye}
      title={t("TableEditorStep6.title")}
      description={t("TableEditorStep6.description")}
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            disabled={!isAnyCellSelected || isHeaderless}
          >
            {t("TableEditorStep6.inputs.headings.label", {
              count: encodedCommonHeaders.length,
            })}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="min-w-56">
          {tableState.cells
            .filter((cell) => cell.role === "th")
            .map((cell) => {
              const cellIdentifier = encodeCellIdentifier(cell);
              const isHeaderSelected = encodedCommonHeaders.some(
                (header) => header === cellIdentifier,
              );

              const topInPx =
                tableState.position.y +
                tableState.rows
                  .slice(0, cell.originRow)
                  .reduce((sum, row) => sum + row.height, 0);

              const leftInPx =
                tableState.position.x +
                tableState.cols
                  .slice(0, cell.originCol)
                  .reduce((sum, column) => sum + column.width, 0);

              const widthInPx = tableState.cols
                .slice(cell.originCol, cell.originCol + cell.colSpan)
                .reduce((sum, col) => sum + col.width, 0);

              const heightInPx = tableState.rows
                .slice(cell.originRow, cell.originRow + cell.rowSpan)
                .reduce((sum, row) => sum + row.height, 0);

              return (
                <DropdownMenuCheckboxItem
                  key={cellIdentifier}
                  checked={isHeaderSelected}
                  onCheckedChange={(checked) => {
                    const addedEncodedHeaders = checked ? [cellIdentifier] : [];
                    const removedEncodedHeaders = checked
                      ? []
                      : [cellIdentifier];

                    dispatch({
                      type: "updateTableCellHeaders",
                      addedEncodedHeaders,
                      removedEncodedHeaders,
                    });
                  }}
                  onSelect={(event) => event.preventDefault()}
                >
                  <div className="flex flex-col gap-2">
                    <span className="text-muted-foreground text-xs font-medium">
                      Zeile {cell.originRow + 1}, Spalte {cell.originCol + 1}
                    </span>
                    <TableCellPreview
                      fragment={fragment}
                      topInPx={topInPx}
                      leftInPx={leftInPx}
                      widthInPx={widthInPx}
                      heightInPx={heightInPx}
                    />
                  </div>
                </DropdownMenuCheckboxItem>
              );
            })}
        </DropdownMenuContent>
      </DropdownMenu>
    </TableEditorStep>
  );
}
